import { useTeamTree } from '@internal/backstage-plugin-common-library-react';
import { useMemo } from 'react';
import { useAsync } from 'react-use';

import { CatalogApi } from '@backstage/catalog-client';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import {
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEntity, catalogApiRef } from '@backstage/plugin-catalog-react';

interface TeamOption {
  id: string;
  name: string;
}

const stripEntityPrefix = (entityRef: string): string => {
  return entityRef.replace(/^(repository|group):default\//, '');
};

// Helper function to get display name with fallback
const getDisplayName = (entity: Entity): string => {
  return entity.metadata.title
    ? `${entity.metadata.title} | ${entity.metadata.name}`
    : entity.metadata.name;
};

// Helper function to extract child team references
const getChildTeamRefs = (entity: Entity): string[] => {
  return (
    entity.relations
      ?.filter(r => r.type === 'parentOf')
      .map(r => r.targetRef) || []
  );
};

async function getOwnedJiraTeams(
  catalogApi: CatalogApi,
  groupId: string,
): Promise<{ id: string; name: string }[]> {
  const result = await catalogApi.getEntities({
    filter: {
      kind: 'Component',
      'spec.type': 'jira-team',
      'relations.ownedBy': [`group:default/${groupId.toLowerCase()}`],
    },
    fields: ['metadata.name', 'metadata.title'],
  });
  return result.items.map(e => ({
    id: e.metadata.name,
    name: (e.metadata.title as string) || e.metadata.name,
  }));
}

// Helper function to get Jira project keys from entity annotations
const getJiraProjectKeys = (entity: Entity): string[] => {
  const jiraProjects = entity.metadata.annotations?.['jira/project-key'];
  if (!jiraProjects) return [];
  return jiraProjects
    .split(',')
    .map(key => key.trim())
    .filter(Boolean);
};

const collectJiraProjectsFromTree = (tree: any): string[] => {
  if (!tree) return [];
  const projectKeys = new Set<string>();
  const traverse = (node: any) => {
    if (!node) return;
    if (node.jira_projects && Array.isArray(node.jira_projects)) {
      node.jira_projects.forEach((key: string) => {
        if (key) projectKeys.add(key);
      });
    }
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach((child: any) => traverse(child));
    }
  };
  traverse(tree);
  return Array.from(projectKeys);
};

// Helper function to get repository options with confirmed ownership
async function getRepositoryOptions(
  entity: Entity,
  catalogApi: CatalogApi,
): Promise<TeamOption[]> {
  const repoEntities = await catalogApi.getEntities({
    filter: {
      kind: ['Repository'],
      'relations.ownedBy': stringifyEntityRef(entity),
      'metadata.annotations.tomtom.com/ownership-status': 'confirmed',
    },
  });
  return repoEntities.items.map(repo => ({
    id: repo.metadata.name,
    name: repo.metadata.name,
  }));
}

async function getDirectChildTeams(
  catalogApi: CatalogApi,
  childTeamRefs: string[],
): Promise<TeamOption[]> {
  if (childTeamRefs.length === 0) return [];
  const response = await catalogApi.getEntitiesByRefs({
    entityRefs: childTeamRefs,
  });
  const directChildTeams: TeamOption[] = [];
  for (const entity of response.items) {
    if (!entity) continue;
    directChildTeams.push({
      id: entity.metadata.name,
      name: getDisplayName(entity),
    });
  }
  return directChildTeams;
}

async function getConfirmedRepoCountsByTeam(
  catalogApi: CatalogApi,
  teamIds: string[],
): Promise<Map<string, number>> {
  const counts = new Map<string, number>();
  if (teamIds.length === 0) return counts;

  const CHUNK_SIZE = 50;
  const chunks: string[][] = [];
  for (let i = 0; i < teamIds.length; i += CHUNK_SIZE) {
    chunks.push(teamIds.slice(i, i + CHUNK_SIZE));
  }

  await Promise.all(
    chunks.map(async chunk => {
      const ownerRefs = chunk.map(id => `group:default/${id}`);
      const repoEntities = await catalogApi.getEntities({
        filter: {
          kind: ['Repository'],
          'relations.ownedBy': ownerRefs,
          'metadata.annotations.tomtom.com/ownership-status': 'confirmed',
        },
      });

      for (const repo of repoEntities.items) {
        const ownerRef = repo.relations?.find(
          r => r.type === 'ownedBy',
        )?.targetRef;
        if (!ownerRef) continue;
        const teamId = ownerRef.split('/').pop()!.toLowerCase();
        counts.set(teamId, (counts.get(teamId) ?? 0) + 1);
      }
    }),
  );

  return counts;
}

export function useGroupContext() {
  const { entity } = useEntity();
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const catalogApi = useApi(catalogApiRef);
  const { teamTree } = useTeamTree(entity.metadata.name);

  const { value: contextData, loading: contextLoading } = useAsync(async () => {
    const { token } = await identityApi.getCredentials();
    const endpointBase = await discoveryApi.getBaseUrl('common-utilities');

    const childTeamRefs = getChildTeamRefs(entity);
    const isParentGroup = childTeamRefs.length > 0;

    const currentEntityJiraKeys = getJiraProjectKeys(entity);
    const treeJiraKeys = teamTree?.tree
      ? collectJiraProjectsFromTree(teamTree.tree)
      : [];
    const allJiraKeys = [
      ...new Set([...currentEntityJiraKeys, ...treeJiraKeys]),
    ];

    if (!isParentGroup) {
      const [repos, ownedJiraTeams] = await Promise.all([
        getRepositoryOptions(entity, catalogApi),
        getOwnedJiraTeams(catalogApi, entity.metadata.name),
      ]);
      return {
        isParentGroup: false,
        childTeams: [] as TeamOption[],
        repos,
        groupName: entity.metadata.name,
        groupChildrenMap: {},
        jiraProjectKeys: allJiraKeys,
        ownedJiraTeams,
      };
    }

    const directChildTeams = await getDirectChildTeams(
      catalogApi,
      childTeamRefs,
    );

    const displayTeams: TeamOption[] = [
      {
        id: stripEntityPrefix(`group:default/${entity.metadata.name}`),
        name: getDisplayName(entity),
      },
      ...directChildTeams,
    ];

    const childTeamsId = directChildTeams.map(childTeam => childTeam.id);

    const flatTreeMap: Record<string, string[]> = {};
    await Promise.all(
      childTeamsId.map(async id => {
        const response = await fetch(
          `${endpointBase}/team-tree/${id.toUpperCase()}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          },
        );
        const data = await response.json();
        flatTreeMap[id.toLowerCase()] = (data.flatTree ?? []).map(
          (subId: string) => subId.toLowerCase(),
        );
      }),
    );

    const allTeamIdsToCheck = new Set<string>([
      entity.metadata.name.toLowerCase(),
      ...childTeamsId.map(id => id.toLowerCase()),
    ]);
    for (const subIds of Object.values(flatTreeMap)) {
      subIds.forEach(subId => allTeamIdsToCheck.add(subId));
    }

    const confirmedRepoCounts = await getConfirmedRepoCountsByTeam(
      catalogApi,
      Array.from(allTeamIdsToCheck),
    );

    const groupChildrenMap: Record<
      string,
      { id: string; tree: string[]; confirmedRepoCount: number }
    > = {};

    groupChildrenMap[entity.metadata.name.toLowerCase()] = {
      id: entity.metadata.name,
      tree: [],
      confirmedRepoCount:
        confirmedRepoCounts.get(entity.metadata.name.toLowerCase()) ?? 0,
    };

    for (const id of childTeamsId) {
      const normalizedId = id.toLowerCase();
      const subIds = flatTreeMap[normalizedId] ?? [];

      const totalCount = subIds.reduce((sum, subId) => {
        return sum + (confirmedRepoCounts.get(subId) ?? 0);
      }, 0);

      groupChildrenMap[normalizedId] = {
        id,
        tree: subIds.filter(subId => subId !== normalizedId),
        confirmedRepoCount: totalCount,
      };
    }

    return {
      isParentGroup: true,
      childTeams: displayTeams,
      repos: [] as TeamOption[],
      groupName: entity.metadata.name,
      groupChildrenMap,
      jiraProjectKeys: allJiraKeys,
      ownedJiraTeams: [] as TeamOption[],
    };
  }, [entity, teamTree, catalogApi]);

  const allTeamIds = useMemo(() => {
    const ids = new Set<string>();
    ids.add(entity.metadata.name.toUpperCase());
    if (teamTree?.flatTree) {
      (teamTree.flatTree as string[]).forEach((id: string) => {
        if (id) ids.add(id.toUpperCase());
      });
    }
    return Array.from(ids);
  }, [entity.metadata.name, teamTree]);

  return {
    ...(contextData || {
      isParentGroup: false,
      childTeams: [] as TeamOption[],
      repos: [] as TeamOption[],
      groupName: entity.metadata.name,
      groupChildrenMap: {},
      jiraProjectKeys: [],
      ownedJiraTeams: [] as TeamOption[],
    }),
    allTeamIds,
    loading: contextLoading,
  };
}
